/* eslint-disable camelcase */
import { Routes } from '@angular/router';
import { Error404Page } from './app-shell/errors/404.component';
import { routesSeo as routesSeoEn } from './locale/en/routes-seo';
import { routesSeo as routesSeoDe } from './locale/de/routes-seo';
import { routesSeo as routesSeoEs } from './locale/es/routes-seo';
import { routesSeo as routesSeoFr } from './locale/fr/routes-seo';
import { routesSeo as routesSeoIt } from './locale/it/routes-seo';
import { routesSeo as routesSeoPt } from './locale/pt/routes-seo';
import { AuthGuard } from './user/auth-guard-activator.service';
import { AdminGuard } from './user/admin-guard.service';
import { UnsavedChangesGuard } from './shared/unsaved-changes-guard/unsaved-changes.guard';
import { TranslationResolver } from './shared/translation/translation.resolver';
import { samplePollResolver } from './whoo/features/sample-poll/sample-poll.resolver';

// Pro Sprache eigener URL-Slug fuer die Sample-Polls-Liste. Wir registrieren
// jeden Slug als eigene Route, sodass die selbe App-Instanz im jeweiligen
// Locale-Build den passenden Pfad serviert.
const samplePollsListPaths: string[] = Array.from(new Set([
    routesSeoEn.samplePollsList,
    routesSeoDe.samplePollsList,
    routesSeoEs.samplePollsList,
    routesSeoFr.samplePollsList,
    routesSeoIt.samplePollsList,
    routesSeoPt.samplePollsList,
]));

const samplePollsListRoutes: Routes = samplePollsListPaths.map(path => ({
    path,
    loadComponent: () =>
        import('./whoo/features/sample-poll/sample-polls-list.component').then(
            (m) => m.SamplePollsListComponent
        ),
    data: { id: 'samplePollsList', noIndex: false },
}));

const samplePollDetailRoutes: Routes = samplePollsListPaths.map(path => ({
    path: path + '/:slug',
    loadComponent: () =>
        import('./whoo/features/sample-poll/sample-poll-vote.page').then(
            (m) => m.SamplePollVotePage
        ),
    resolve: { samplePollKeys: samplePollResolver },
    data: { id: 'samplePoll', noIndex: false },
}));

// Pro Sprache: {listing-slug}/{slug}/{matrix-suffix}. Beide Teile sind
// sprachspezifisch (z.B. /umfrage-vorlagen/baby-shower-invitation/ergebnisse
// bzw. /poll-templates/baby-shower-invitation/results).
const samplePollMatrixPathPairs: { listing: string; suffix: string }[] = [
    { listing: routesSeoEn.samplePollsList, suffix: routesSeoEn.samplePollMatrixSuffix },
    { listing: routesSeoDe.samplePollsList, suffix: routesSeoDe.samplePollMatrixSuffix },
    { listing: routesSeoEs.samplePollsList, suffix: routesSeoEs.samplePollMatrixSuffix },
    { listing: routesSeoFr.samplePollsList, suffix: routesSeoFr.samplePollMatrixSuffix },
    { listing: routesSeoIt.samplePollsList, suffix: routesSeoIt.samplePollMatrixSuffix },
    { listing: routesSeoPt.samplePollsList, suffix: routesSeoPt.samplePollMatrixSuffix },
];

const samplePollMatrixRoutes: Routes = Array.from(
    new Map(samplePollMatrixPathPairs.map(p => [`${p.listing}/${p.suffix}`, p])).values()
).map(p => ({
    path: p.listing + '/:slug/' + p.suffix,
    loadComponent: () =>
        import('./whoo/features/sample-poll/sample-poll-matrix.page').then(
            (m) => m.SamplePollMatrixPage
        ),
    resolve: { samplePollKeys: samplePollResolver },
    data: { id: 'samplePollMatrix', noIndex: true },
}));


export const appRoutes: Routes = [
    {
        path: '',
        loadChildren: () =>
            import('./whoo/features/my-polls/my-polls.module').then(
                (m) => m.MyPollsModule
            ),
        canActivate: [AuthGuard],
        data: { id: 'myPolls', noIndex: true },
    },
    {
        path: routesSeoEn.index,
        redirectTo: '/',
        pathMatch: 'full',
        data: { id: 'homeTemplates', noIndex: true },
    },
    {
        path: routesSeoEn['choosePollTemplate'],
        loadComponent: () =>
            import('./whoo/features/create-and-edit-poll/choose-poll-template/choose-poll-template.component').then(
                (m) => m.ChoosePollTemplateComponent
            ),
        data: { id: 'choosePollTemplate', noIndex: true },
    },
    {
        path: routesSeoEn['404'],
        component: Error404Page,
        data: { id: '404', noIndex: true },
    },

    {
        path: routesSeoEn['feedback'],
        loadComponent: () =>
            import('./app-shell/footer/feedback-page/feedback.page').then(
                (m) => m.FeedbackPage
            ),
        data: { id: 'feedback', noIndex: true },
    },
    {
        path: routesSeoEn['admin'],
        loadComponent: () =>
            import('./app-shell/admin/admin.page').then((m) => m.AdminPage),
        canActivate: [AdminGuard],
        data: { id: 'admin', noIndex: true },
    },
    {
        path: 'test',
        loadComponent: () =>
            import('./app-shell/test/test-vote/test-vote-page.component').then(
                (m) => m.TestVotePage
            ),
        data: { id: 'testPage', noIndex: true },
    },
    {
        path: 'test' + '/:ownerId/:pollId',
        loadComponent: () =>
            import('./app-shell/test/test-vote/test-vote-page.component').then(
                (m) => m.TestVotePage
            ),
        canActivate: [AuthGuard],
        data: { id: 'testPage', noIndex: true },
    },


    {
        path: 'test-image-upload',
        loadComponent: () =>
            import('./app-shell/test/test-image-upload/test-image-upload.component').then(
                (m) => m.TestImageUploadPage
            ),
        data: { id: 'testImageUpload', noIndex: true },
    },
    {
        path: 'test-image-upload' + '/:ownerId/:pollId',
        loadComponent: () =>
            import('./app-shell/test/test-image-upload/test-image-upload.component').then(
                (m) => m.TestImageUploadPage
            ),
        canActivate: [AuthGuard],
        data: { id: 'testImageUpload', noIndex: true },
    },

    {
        path: 'test' + '/:ownerId/:pollId/:voteUserId',
        loadComponent: () =>
            import('./app-shell/test/test-vote/test-vote-page.component').then(
                (m) => m.TestVotePage
            ),
        canActivate: [AuthGuard],
        data: { id: 'testPage', noIndex: true },
    },

    {
        path: 'test-time-period',
        loadComponent: () =>
            import('./app-shell/test/test-time-period/test-time-period-page.component').then(
                (m) => m.TestTimePeriodPage
            ),
        data: { id: 'testPage', noIndex: true },
    },
    {
        path: 'test-time-period' + '/:ownerId/:pollId',
        loadComponent: () =>
            import('./app-shell/test/test-time-period/test-time-period-page.component').then(
                (m) => m.TestTimePeriodPage
            ),
        canActivate: [AuthGuard],
        data: { id: 'testPage', noIndex: true },
    },
    {
        path: routesSeoEn['myPolls'],
        loadChildren: () =>
            import('./whoo/features/my-polls/my-polls.module').then(
                (m) => m.MyPollsModule
            ),
        canActivate: [AuthGuard],
        data: { id: 'myPolls', noIndex: true },
    },
    {
        path: routesSeoEn['pollResponses'] + '/:ownerId/:pollId',
        loadComponent: () =>
            import('./whoo/features/poll-results/charts-and-matrix-page/charts-and-matrix.page').then(
                (m) => m.ChartsAndMatrixPage
            ),
        canActivate: [AuthGuard],
        data: { id: 'pollResponses', noIndex: true },
    },
    {
        path: routesSeoEn['pollMatrix'] + '/:ownerId/:pollId',
        loadComponent: () =>
            import('./whoo/features/poll-results/matrix-page/results-matrix.page').then((m) => m.PollResultsMatrixPage),
        data: { id: 'pollMatrix', noIndex: true },
    },
    {
        path: routesSeoEn['createPoll'],
        loadComponent: () =>
            import('./whoo/features/create-and-edit-poll/create-poll/create-poll.component').then(
                (m) => m.CreatePollComponent
            ),
        data: { id: 'createPoll', noIndex: true },
    },
    {
        path: routesSeoEn['createPoll'] + '/:pollTitle',
        loadComponent: () =>
            import('./whoo/features/create-and-edit-poll/create-poll/create-poll.component').then(
                (m) => m.CreatePollComponent
            ),
        data: { id: 'createPoll', noIndex: true },
    },
    {
        path: routesSeoEn['editPoll'] + '/:stepIndex/:ownerId/:pollId',
        loadComponent: () =>
            import('./whoo/features/create-and-edit-poll/edit-poll/edit-poll.component').then(
                (m) => m.EditPollComponent
            ),
        canActivate: [AuthGuard],
        data: { id: 'editPoll', noIndex: true },
    },
    {
        // participant link slug (concept-custom-participant-links-2026-10-28 §5.1): ONE segment is a slug, two or
        // three are ids - the same split as the ogProxy parser (report S2 §6.2). Kept before the id routes on purpose
        path: routesSeoEn['vote'] + '/:voteSlug',
        loadComponent: () =>
            import('./whoo/features/vote/vote-page/vote.page').then(
                (m) => m.VotePage
            ),
        canDeactivate: [UnsavedChangesGuard],
        data: { id: 'vote', noIndex: true },
    },
    {
        path: routesSeoEn['vote'] + '/:ownerId/:pollId',
        loadComponent: () =>
            import('./whoo/features/vote/vote-page/vote.page').then(
                (m) => m.VotePage
            ),
        canDeactivate: [UnsavedChangesGuard],
        data: { id: 'vote', noIndex: true },
    },
    {
        path: routesSeoEn['vote'] + '/:ownerId/:pollId/:voteUserId',
        loadComponent: () =>
            import('./whoo/features/vote/vote-page/vote.page').then(
                (m) => m.VotePage
            ),
        canDeactivate: [UnsavedChangesGuard],

        data: { id: 'vote', noIndex: true },
    },
    {
        path: routesSeoEn['votePreview'] + '/:ownerId/:pollId',
        loadComponent: () =>
            import('./whoo/features/vote/vote-preview/vote-preview.component').then(
                (m) => m.VotePreviewComponent
            ),
        canActivate: [AuthGuard],
        data: { id: 'votePreview', noIndex: true, translationScopes: ['themes'] },
        resolve: { translation: TranslationResolver }
    },
    {
        path: routesSeoEn['votePreview'] + '/:ownerId/:pollId/:voteUserId',
        loadComponent: () =>
            import('./whoo/features/vote/vote-preview/vote-preview.component').then(
                (m) => m.VotePreviewComponent
            ),
        canActivate: [AuthGuard],
        data: { id: 'votePreview', noIndex: true, translationScopes: ['themes'] },
        resolve: { translation: TranslationResolver }

    },
    {
        path: routesSeoEn['voteResponse'] + '/:ownerId/:pollId/:voteUserId?',
        loadComponent: () =>
            import('./whoo/features/vote/vote-response/vote-response.component').then(
                (m) => m.VoteResponseComponent
            ),
        data: { id: 'voteResponse', noIndex: true },
    },
    {
        path: routesSeoEn['votePreviewResponse'] + '/:ownerId/:pollId/:voteUserId?',
        loadComponent: () =>
            import('./whoo/features/vote/vote-response/vote-response.component').then(
                (m) => m.VoteResponseComponent
            ),
        canActivate: [AuthGuard],
        data: { id: 'votePreviewResponse', noIndex: true },
    },
    {
        path: 'user',
        loadChildren: () =>
            import('./user/user.module').then((m) => m.UserModule),
        data: { id: 'user', noIndex: true },
    },
    // Sample-Polls: Liste + Detail + Matrix, jeweils pro Sprache als eigene
    // URL-Route registriert (siehe samplePollsListPaths weiter oben).
    // Matrix VOR Detail eintragen, damit Router auf 3-Segment-URLs nicht
    // versehentlich auf die Detail-Route mit "results" als Slug auflaeuft.
    ...samplePollsListRoutes,
    ...samplePollMatrixRoutes,
    ...samplePollDetailRoutes,
    { path: '**', component: Error404Page, data: { id: '404', noIndex: true } }
];

